/**
 * EL-185 — o número de pedidos de exclusão AGUARDANDO DECISÃO, no item "Pedidos" da barra
 * lateral do Admin Hype.
 *
 * POR QUE ISTO EXISTE (lote de 09/10/2026, item 6 — "Em EL-185, indique quando há pedidos
 * novos.")
 *   O contador já existia, mas era lido UMA vez, ao montar a casca — e a casca vive a sessão
 *   inteira. Um pedido que chegava com o admin logado só aparecia depois de F5; e, pior,
 *   depois de Aprovar/Recusar/Restaurar o número antigo FICAVA lá, mesmo com a fila zerada.
 *   Deixar o número só mais visível deixaria mais visível o número errado.
 *
 *   Decisões do usuário: "novo" é todo pedido ainda pendente (sem guardar "visto" — um
 *   pedido esquecido continua chamando atenção, porque é trabalho por fazer); o aviso é SÓ o
 *   número em laranja (sem toast, sem título da aba, sem e-mail).
 *
 * Este módulo é PURO (sem DOM) de propósito: os testes rodam em Node, e a parte que pode dar
 * errado em silêncio — duas leituras no ar ao mesmo tempo, uma resposta que chega depois de
 * a casca morrer, uma falha de rede apagando o número — é a coordenação, não o desenho.
 * Quem desenha e quem marca o relógio é `casca.ts`.
 */

/** De quanto em quanto tempo a casca reconta com a aba visível. Ao voltar para a aba, na hora. */
export const SONDA_PEDIDOS_MS = 60_000;

/** O mínimo que a recontagem lê de cada pedido pendente. */
export interface PendenteLido {
  id: string;
  solicitadoEm: string;
}

export interface ResumoFila {
  total: number;
  /**
   * Os ids pendentes, ordenados e juntos: a IDENTIDADE da fila. Só o total não percebe
   * "um decidido + um novo" entre duas leituras — o número fica igual e a fila mudou.
   */
  assinatura: string;
}

export function resumirFila(pendentes: readonly PendenteLido[]): ResumoFila {
  const ids = [...new Set(pendentes.map((p) => p.id))].sort();
  return { total: ids.length, assinatura: ids.join(',') };
}

/** Sem leitura anterior, sempre mudou: a primeira resposta é a que pinta o estado inicial. */
export function filaMudou(antes: ResumoFila | null, agora: ResumoFila): boolean {
  return antes === null || antes.assinatura !== agora.assinatura;
}

/** O que vai escrito na bolinha. Vazio = a bolinha some. Acima de 99 não cabe no item. */
export function textoDoContador(total: number): string {
  if (!(total > 0)) return '';
  return total > 99 ? '99+' : String(Math.trunc(total));
}

/**
 * O nome do item para o leitor de tela e para a dica do mouse. No trilho estreito (≤900px)
 * rótulo e número somem e sobra só o ícone com um ponto: sem isto o botão ficaria mudo.
 * Sem ID no texto (regra da casa: nenhum texto de tela cita EL-xxx).
 */
export function rotuloDoItem(total: number): string {
  const t = textoDoContador(total);
  return t === '' ? 'Pedidos de exclusão' : `Pedidos de exclusão — ${t} aguardando decisão`;
}

export interface Recontagem {
  /**
   * Reconta. Se já há uma leitura no ar, NÃO abre outra: marca para repetir UMA vez quando
   * ela voltar e devolve a mesma promessa. Nunca rejeita.
   */
  pedir(): Promise<void>;
  /** Depois disto nada mais pinta — nem a leitura que ainda estiver no ar. */
  parar(): void;
}

/**
 * A coordenação da recontagem.
 *
 * - TRAVA DE REENTRADA: a montagem, cada troca de tela, o relógio e a volta para a aba
 *   pedem recontagem. Sem a trava, duas leituras se sobrepõem e a mais velha pode chegar
 *   por último, pintando o número de antes.
 * - REPETIR EM VEZ DE DESCARTAR: o pedido que chega com uma leitura no ar não pode ser
 *   jogado fora. É o caso de Aprovar: a leitura do relógio saiu ANTES da decisão e voltaria
 *   com o pedido ainda pendente; descartar a recontagem da tela nova deixaria o número velho.
 * - FALHA NÃO APAGA: um soluço de rede mantém o último número conhecido (some só o que foi
 *   decidido, não o que não deu para ler). A falha já chega ao monitor de conexão por quem lê.
 * - PINTA SÓ QUANDO A FILA MUDA: a sonda roda a sessão inteira, inclusive dentro do cenário;
 *   mexer no DOM a cada minuto sem motivo é ruído.
 */
export function criarRecontagem(
  ler: () => Promise<readonly PendenteLido[]>,
  pintar: (r: ResumoFila) => void,
): Recontagem {
  let ultimo: ResumoFila | null = null;
  let corrente: Promise<void> | null = null;
  let deNovo = false;
  let parado = false;

  async function rodar(): Promise<void> {
    do {
      deNovo = false;
      let agora: ResumoFila;
      try {
        agora = resumirFila(await ler());
      } catch {
        continue; // mantém o último número; repete só se alguém pediu no meio tempo
      }
      if (parado) return;
      if (filaMudou(ultimo, agora)) {
        ultimo = agora;
        try {
          pintar(agora);
        } catch {
          /* desenhar o número não pode derrubar a barra lateral */
        }
      }
    } while (deNovo && !parado);
  }

  return {
    pedir() {
      if (parado) return Promise.resolve();
      if (corrente !== null) {
        deNovo = true;
        return corrente;
      }
      corrente = rodar().finally(() => {
        corrente = null;
      });
      return corrente;
    },
    parar() {
      parado = true;
    },
  };
}
